import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Box,
    IconButton,
    InputAdornment,
    CircularProgress,
    Typography,
    Divider,
    Paper,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { useToast } from '../toast/useToast';
import {
    useMqttCredentialsQuery,
    useDownloadZigbeeConfigMutation,
    useDownloadBootstrapConfigMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';

interface MqttCredentialsDialogProps {
    open: boolean;
    onClose: () => void;
    objektId: number;
    objektNazev: string;
}

const MqttCredentialsDialog: React.FC<MqttCredentialsDialogProps> = ({
    open,
    onClose,
    objektId,
    objektNazev,
}) => {
    const { showToast } = useToast();

    const { data: credentials, isLoading } = useMqttCredentialsQuery(open);
    const downloadConfigMutation = useDownloadZigbeeConfigMutation();
    const downloadBootstrapMutation = useDownloadBootstrapConfigMutation();

    const handleCopy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`${label} zkopirováno do schránky`, 'success');
        } catch {
            showToast('Nepodařilo se zkopírovat do schránky', 'error');
        }
    };

    const getSafeFileName = () => {
        return objektNazev
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
            .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special chars
            .replace(/\s+/g, '-')
            .toLowerCase()
            || 'objekt';
    };

    const handleDownloadConfig = () => {
        downloadConfigMutation.mutate(objektId, {
            onSuccess: (configContent) => {
                const blob = new Blob([configContent], { type: 'text/yaml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `configuration-${getSafeFileName()}.yaml`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                showToast('Zigbee2MQTT konfigurace stažena', 'success');
            },
        });
    };

    const handleDownloadBootstrapConfig = () => {
        downloadBootstrapMutation.mutate(objektId, {
            onSuccess: (configContent) => {
                const blob = new Blob([configContent], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `jsdh-config.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                showToast('Bootstrap konfigurace stažena', 'success');
            },
        });
    };

    const baseTopic = credentials
        ? `zigbee2mqtt/${credentials.username}/o${objektId}`
        : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Konfigurace RPi - {objektNazev}</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Stáhněte konfigurační soubor pro Raspberry Pi.
                            MQTT heslo bude automaticky vloženo do konfigurace.
                        </Alert>

                        <TextField
                            label="MQTT Broker"
                            value={credentials?.brokerUrl || ''}
                            fullWidth
                            margin="normal"
                            disabled
                            InputProps={{
                                endAdornment: credentials?.brokerUrl && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => handleCopy(credentials.brokerUrl, 'Broker URL')}
                                            size="small"
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="MQTT Username"
                            value={credentials?.username || ''}
                            fullWidth
                            margin="normal"
                            disabled
                            InputProps={{
                                endAdornment: credentials?.username && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => handleCopy(credentials.username, 'Username')}
                                            size="small"
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="Base Topic"
                            value={baseTopic}
                            fullWidth
                            margin="normal"
                            disabled
                            InputProps={{
                                endAdornment: baseTopic && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => handleCopy(baseTopic, 'Base Topic')}
                                            size="small"
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Base topic pro tento objekt (automaticky v konfiguračních souborech)"
                        />

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Stažení konfigurace
                        </Typography>

                        {/* Automatická instalace */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Automatická instalace (doporučeno)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Konfigurační soubor pro bootstrap skript na Raspberry Pi.
                                        Automaticky nastaví Zigbee2MQTT a připojení k serveru.
                                    </Typography>
                                </Box>
                                <Button
                                    onClick={handleDownloadBootstrapConfig}
                                    variant="contained"
                                    size="small"
                                    startIcon={
                                        downloadBootstrapMutation.isPending ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <DownloadIcon />
                                        )
                                    }
                                    disabled={downloadBootstrapMutation.isPending}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    jsdh-config.json
                                </Button>
                            </Box>
                        </Paper>

                        {/* Manuální konfigurace */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Manuální konfigurace
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Konfigurační soubor pro Zigbee2MQTT.
                                        Pro ruční instalaci zkopírujte do /opt/zigbee2mqtt/data/.
                                    </Typography>
                                </Box>
                                <Button
                                    onClick={handleDownloadConfig}
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        downloadConfigMutation.isPending ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <DownloadIcon />
                                        )
                                    }
                                    disabled={downloadConfigMutation.isPending}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    configuration.yaml
                                </Button>
                            </Box>
                        </Paper>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MqttCredentialsDialog;
